import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/qr-code/qr-code.js';

// Phase Y.C — reusable QR card for an order's customer tracking link.
//
// Encodes the full {origin}/track/{token} URL so a customer can scan
// from a receipt + land on the tracking page. Renders consistently
// across the three operator surfaces that show the QR:
//
//   - Active orders card (inline expand)
//   - Active order detail page
//   - Cashier post-sale confirmation
//
// The component is purely presentational — takes a token, builds the
// URL from window.location.origin (so dev / prod / preview all work
// without config), renders the <sl-qr-code> + a small caption with
// the day-sequence number for the cashier to read aloud.
@customElement('order-qr')
export class OrderQr extends LitElement {
  // 12-char Crockford base32 track token stamped on the sale at
  // create time (Phase Y.A). Empty disables render.
  @property({ type: String }) token = '';

  // Cashier-visible per-day number ("#42"). 0 hides the caption.
  @property({ type: Number, attribute: 'day-sequence' }) daySequence = 0;

  // Visual size in px. The Shoelace QR scales the matrix to fit.
  // Defaults to 200 — clear from arm's length, fits a receipt panel.
  @property({ type: Number }) size = 200;

  // Optional small caption above the QR ("Scan to track your order").
  // Defaulted on; set to "" to hide.
  @property({ type: String }) caption = 'Scan to track your order';

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 16px;
      background: white;
      border-radius: var(--sl-border-radius-medium);
      border: 1px solid var(--app-border, var(--sl-color-neutral-200));
    }
    /* Background stays white in dark mode so the QR contrast holds —
       scanners want a black-on-white code. We adjust the surrounding
       chrome only. */
    .sl-theme-dark :host {
      border-color: rgba(255, 255, 255, 0.08);
    }
    .caption {
      font-family: var(
        --font-mono,
        ui-monospace,
        SFMono-Regular,
        Menlo,
        monospace
      );
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--sl-color-neutral-700);
      text-align: center;
    }
    .day-seq {
      font-family: var(--font-mono);
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      font-size: 1.4rem;
      color: var(--sl-color-neutral-900);
      letter-spacing: -0.02em;
      margin-top: 4px;
    }
    .day-seq .hash {
      opacity: 0.4;
      margin-right: 1px;
    }
    .token {
      font-family: var(--font-mono);
      font-size: 0.7rem;
      letter-spacing: 0.08em;
      color: var(--sl-color-neutral-500);
      word-break: break-all;
      text-align: center;
    }
    sl-qr-code {
      --background-color: white;
      --fill-color: black;
    }
  `;

  private get _trackUrl(): string {
    return `${window.location.origin}/track/${this.token}`;
  }

  render() {
    if (!this.token) return html``;
    return html`
      ${this.caption ? html`<div class="caption">${this.caption}</div>` : ''}
      <sl-qr-code
        value=${this._trackUrl}
        size=${this.size}
        label="Order tracking QR code"
      ></sl-qr-code>
      ${
        this.daySequence > 0
          ? html`<div class="day-seq">
              <span class="hash">#</span>${this.daySequence}
            </div>`
          : ''
      }
      <div class="token">${this.token}</div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'order-qr': OrderQr;
  }
}
